<template>
  <AppCard
    elevation="0"
    data-locator="subscription-consumption-dataroom-storage"
  >
    <!-- Title -->
    <p class="mb-1 text--medium font-weight-bold text-text-primary">
      {{ $t(`${TRANSLATION_KEY_PREFIX}.title`) }}
    </p>

    <template v-if="subscriptionDataroomStorageError">
      <app-alert-in-page :dismissible="false"
                         variant="error"
                         no-icon
                         class="mt-4"
      >
        {{ $t(`${TRANSLATION_KEY_PREFIX}.error`) }}
      </app-alert-in-page>
    </template>
    <template v-else>
      <div v-if="subscriptionDataroomStoragePending" class="mt-4" data-locator="loading-state">
        <v-skeleton-loader type="text"/>
      </div>
      <template v-else-if="subscriptionDataroomStorage">
        <!-- Storage usage -->
        <div class="d-flex align-center justify-space-between mt-5 mb-2">
          <span class="text--small text--bold text-text-primary">
            {{ $t(`${TRANSLATION_KEY_PREFIX}.storage`) }}
          </span>
          <div class="d-flex align-center column-gap-1 text--x-small text-text-tertiary"
               :class="{ 'text-error': isSubscriptionDataroomStorageExceeded }"
          >
            <app-material-icon
              v-if="isSubscriptionDataroomStorageExceeded"
              name="emergency_home"
              size="sm"
              color="icon-danger"
            />
            <span>
              {{ subscriptionDataroomStorage.currentStorage }} Go
              <i18n-t keypath="subscriptions.views.SubscriptionConsumption.DataroomStorage.used"
                      tag="span"
                      :plural="subscriptionDataroomStorage.currentStorage"
              />
              {{ subscriptionDataroomStorage.limitGb }} Go
            </span>
          </div>
        </div>

        <!-- Data visualisation -->
        <SubscriptionDataroomStorageBar :segments="subscriptionDatarooms"/>

        <!-- Datarooms legend -->
        <div class="d-flex flex-wrap column-gap-3 row-gap-2 mt-5">
          <div v-for="(dr, index) in displayedSubscriptionDatarooms" :key="index"
               class="d-flex align-center column-gap-2 text--small text-text-secondary"
          >
            <div class="SubscriptionConsumptionDrPlanUsage__dot"
                 :style="{ backgroundColor: `rgb(var(--v-theme-${dr.color}))` }"
            />
            <span class="text-truncate" style="max-width: 220px;">{{ dr.name }} ({{ dr.storage }} Go)</span>
          </div>
        </div>

        <!-- See more / see less -->
        <app-button v-if="subscriptionDatarooms.length > MAX_DATAROOMS_TO_DISPLAY"
                    type="ghost"
                    variant="neutral"
                    size="small"
                    class="mt-4"
                    data-locator="dataroom-storage-toggle"
                    @click="displayAll = !displayAll"
        >
          {{ $t(`${TRANSLATION_KEY_PREFIX}.${displayAll ? 'seeLess' : 'seeMore'}`) }}
          <app-material-icon :name="displayAll ? 'keyboard_arrow_up' : 'keyboard_arrow_down'" size="sm" />
        </app-button>

        <!-- Share by email -->
        <div class="mt-12">
          <app-button type="outlined"
                      variant="neutral"
                      data-locator="dataroom-storage-share"
                      @click="openShareSubscriptionConsumptionDialog"
          >
            <app-material-icon name="mail" size="sm" />
            {{ $t(`${TRANSLATION_KEY_PREFIX}.shareByEmail`) }}
          </app-button>
        </div>
      </template>
    </template>

    <!-- DIALOGS -->
    <ShareSubscriptionConsumptionDialog v-if="shareSubscriptionConsumptionDialogIsOpen"
                                        :title="shareSubscriptionConsumptionDialogTitle"
                                        :submit-action="SHARE_SUBSCRIPTION_DATAROOM_PLAN"
                                        :ok-loading="shareSubscriptionDataroomPlanPending"
                                        @close="closeShareSubscriptionConsumptionDialog"
    />
  </AppCard>
</template>

<script>
  import { mapState, mapActions } from 'vuex'

  import AppCard from '@/common/AppCard.vue'
  import { DATA_VISUALISATION_COLORS } from '@/common/utils/colors'
  import { toast } from '@/common/utils/toast'
  import {
    GET_SUBSCRIPTION_DATAROOM_STORAGE,
    SHARE_SUBSCRIPTION_DATAROOM_PLAN,
  } from '@/store/modules/subscription/action_types'
  import ShareSubscriptionConsumptionDialog from '@/subscriptions/views/ShareSubscriptionConsumptionDialog.vue'
  import SubscriptionDataroomStorageBar from '@/subscriptions/views/SubscriptionDataroomStorageBar.vue'

  const TRANSLATION_KEY_PREFIX = 'subscriptions.views.SubscriptionConsumption.DataroomStorage'
  const MAX_DATAROOMS_TO_DISPLAY = 10

  export default {
    name: 'SubscriptionConsumptionDrPlanUsage',
    components: { ShareSubscriptionConsumptionDialog, AppCard, SubscriptionDataroomStorageBar },
    data () {
      return {
        DATA_VISUALISATION_COLORS,
        TRANSLATION_KEY_PREFIX,
        MAX_DATAROOMS_TO_DISPLAY,
        displayAll: false,
        shareSubscriptionConsumptionDialogIsOpen: false,
      }
    },
    computed: {
      ...mapState('subscription', [
        'subscriptionDataroomStoragePending',
        'subscriptionDataroomStorage',
        'subscriptionDataroomStorageError',
        'shareSubscriptionDataroomPlanPending',
      ]),
      isSubscriptionDataroomStorageExceeded () {
        return this.subscriptionDataroomStorage?.currentStorage > Number.parseFloat(this.subscriptionDataroomStorage.limitGb)
      },
      subscriptionDatarooms () {
        const { datarooms } = this.subscriptionDataroomStorage
        const colors = DATA_VISUALISATION_COLORS
        datarooms.forEach((dataroom, index) => {
          const colorIndex = index % colors.length
          dataroom.color = colors[colorIndex]
          dataroom.percent = (dataroom.storage / Number.parseInt(this.subscriptionDataroomStorage.limitGb)) * 100
        })
        return datarooms
      },
      displayedSubscriptionDatarooms () {
        return this.displayAll
          ? this.subscriptionDatarooms
          : this.subscriptionDatarooms.slice(0, MAX_DATAROOMS_TO_DISPLAY)
      },
      shareSubscriptionConsumptionDialogTitle () {
        return this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.title', {
          itemName: this.$t(`${TRANSLATION_KEY_PREFIX}.title`),
        })
      },
    },
    created () {
      this.getSubscriptionDataroomStorage()
    },
    methods: {
      ...mapActions('subscription', [GET_SUBSCRIPTION_DATAROOM_STORAGE, SHARE_SUBSCRIPTION_DATAROOM_PLAN]),
      async getSubscriptionDataroomStorage () {
        try {
          await this.GET_SUBSCRIPTION_DATAROOM_STORAGE()
        } catch {
          toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.error`))
        }
      },
      openShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = true
      },
      closeShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = false
      },
    },
  }
</script>

<style scoped lang="scss">
  .SubscriptionConsumptionDrPlanUsage__dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
  }
</style>
